<template>
  <BContainer>
    <BRow align-v="center">
      <BCol
        lg="6"
        class="pr-lg-5"
      >
        <h2 class="my-4">There’s a world of differences out there</h2>
        <p>A strong consumer–brand relationship can be a top reason that cements global—rather than local—brand loyalty.</p>
        <GreyBox>
          <span><strong>{{ numbers.brandOrigin }}%</strong> of global consumers say that brand origin is a key purchase&nbsp;driver<sup>3</sup></span>
        </GreyBox>
        <p>Effective visual experiences can emotionally tie viewers to brands. Any savvy global marketer should consider regional differences as they craft visual stories, and it will be key to ensure media caters to users’ preferred means of access around the world. While Chrome is the clear usage winner at 56.6%—and Safari a distant second at 14.7%—marketers must not overlook other browsers used around the world.</p>
      </BCol>
      <BCol
        lg="6"
        class="pl-lg-5"
      >
        <strong>Global browser use</strong>
        <br>
        <span class="label-group">
          <label class="chrome">Chrome</label>
          <label class="safari">Safari</label>
          <label class="other">Other</label>
        </span>
        <BRow class="no-gutters">
          <BCol
            cols="6"
            sm="4"
            class="text-center"
            align-h="center"
          >
            <FullDonutCountry
              :nums="numbers.pies.US"
              abbr="US"
              chart-id="ChartDonutCountry-US"
            />
          </BCol>
          <BCol
            cols="6"
            sm="4"
            class="text-center"
            align-h="center"
          >
            <FullDonutCountry
              :nums="numbers.pies.UK"
              abbr="UK"
              chart-id="ChartDonutCountry-UK"
            />
          </BCol>
          <BCol
            cols="6"
            sm="4"
            class="text-center"
            align-h="center"
          >
            <FullDonutCountry
              :nums="numbers.pies.DE"
              abbr="DE"
              chart-id="ChartDonutCountry-DE"
            />
          </BCol>
          <BCol
            cols="6"
            sm="4"
            class="text-center"
            align-h="center"
          >
            <FullDonutCountry
              :nums="numbers.pies.IN"
              abbr="IN"
              chart-id="ChartDonutCountry-IN"
            />
          </BCol>
          <BCol
            cols="6"
            sm="4"
            class="text-center"
            align-h="center"
          >
            <FullDonutCountry
              :nums="numbers.pies.JP"
              abbr="JP"
              chart-id="ChartDonutCountry-JP"
            />
          </BCol>
          <BCol
            cols="6"
            sm="4"
            class="text-center"
            align-h="center"
            align-self="end"
          >
            <v-popover
              popover-class="pp-100"
              placement="right-end"
              boundaries-element="window"
            >
              <LearnMore class="learn-bottom" />
              <template slot="popover">
                <BRow>
                  <BCol>
                    <div class="popover-title">
                      <cld-image
                        class="tooltip-finger-icon"
                        public-id="v1557870041/state_of_visual_media/cloudinary-finger.svg"
                      />
                      <strong>Second-tier browser use by region:</strong>
                    </div>
                  </BCol>
                </BRow>
                <BRow>
                  <BCol>
                    <span class="label-group">
                      <label class="firefox">Firefox</label>
                      <label class="edge">Edge</label>
                      <label class="ie">IE</label>
                      <label class="opera">Opera</label>
                      <label class="operamini">Opera Mini</label>
                      <label class="other">Other</label>
                    </span>
                  </BCol>
                </BRow>
                <b-row>
                  <BCol
                    class="text-center"
                    align-h="center"
                  >
                    <h4>Canada</h4>
                    <FullDonutCountry
                      :nums="numbers['more-pies'].CA"
                      :dark="true"
                      abbr="CA"
                      :which="2"
                      chart-id="ChartDonutCountry-CA"
                    />
                  </BCol>
                  <BCol
                    class="text-center"
                    align-h="center"
                  >
                    <h4>Saudi Arabia</h4>
                    <FullDonutCountry
                      :nums="numbers['more-pies'].SAU"
                      :dark="true"
                      abbr="SAU"
                      :which="2"
                      chart-id="ChartDonutCountry-SAU"
                    />
                  </BCol>
                  <BCol
                    class="text-center"
                    align-h="center"
                  >
                    <h4>Israel</h4>
                    <FullDonutCountry
                      :nums="numbers['more-pies'].IL"
                      :dark="true"
                      abbr="IL"
                      :which="2"
                      chart-id="ChartDonutCountry-IL"
                    />
                  </BCol>
                  <BCol
                    class="text-center"
                    align-h="center"
                  >
                    <h4>Norway</h4>
                    <FullDonutCountry
                      :nums="numbers['more-pies'].NO"
                      :dark="true"
                      abbr="NO"
                      :which="2"
                      chart-id="ChartDonutCountry-NO"
                    />
                  </BCol>
                  <BCol>
                    <span>&nbsp;</span>
                  </BCol>
                </b-row>
              </template>
            </v-popover>
          </BCol>
        </BRow>
      </BCol>
    </BRow>
    <footer>
      <p class="small">
        Source: Nielsen Company
      </p>
    </footer>
  </BContainer>
</template>

<script>
import LearnMore from "../LearnMore.vue";
import GreyBox from "../greybox";
import FullDonutCountry from "../FullDonutCountry.vue";

export default {
  components: {
    LearnMore,
    GreyBox,
    FullDonutCountry
  },
  props: {
    numbers: {
      type: Object,
      default: () => {
        return {};
      }
    },
    dataDesktopMobile: {
      type: [Object, Array],
      default: () => [0]
    }
  }
};
</script>

<style lang="scss" scoped>
aside {
  &:before {
    @include triangle(top, $lightgrey, 8px);
    position: absolute;
    left: 10%;
    bottom: 100%;
  }
}
.label-group {
  display: flex;
  margin-bottom: 1em;
}
label {
  flex: 1;
  /* display: inline-block; */
  font-size: 0.75em;
  text-align: center;
  padding: 0.25em 1em;
  margin-top: 0.25em;
  margin-bottom: 0.25em;
  margin-right: 1em;
  white-space: nowrap;

  &.chrome,
  &.firefox {
    color: white;
    background: $blue;
  }
  &.edge {
    color: white;
    background: $blue2;
  }
  &.ie {
    background: $orange;
  }
  &.opera {
    color: black;
    background: $yellow;
  }
  &.safari,
  &.operamini {
    color: black;
    background: $gold;
  }
  &.other {
    color: black;
    background: $lightgrey2;
  }
}
h4 {
  white-space: nowrap;
  color: $blue;
}
.popover .chart-donut {
  margin-left: auto !important;
}
</style>
